import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
// Layout
import DashboardLayout from "layouts/DashboardLayout";
// Custom Components
import DashboardNavbar from "components/DashboardNavbar";
import Footer from "components/Footer";
// import MDBox from "components/MDBox";
import MaterialTablePicker from "components/MaterialTablePicker";

// Material UI components
import Grid from "@mui/material/Grid2";
// Redux Actions
import setAlert from "store/actions/alertActions";
import {
  getLocations,
  setLoading,
  clearErrors,
  getUserPartnerLocations,
  publishUnpublishLoc,
} from "store/actions/locationAction";
import { getPdf, getCsv } from "store/actions/exportAction";
import MDButton from "components/MDButton";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import TierPicker from "components/AutoComplete/TierPicker";
import Danger from "components/Danger";
import columnsMap from "components/MaterialTablePicker/TableColumns";

// const MaterialTablePicker = React.lazy(() => import("components/MaterialTablePicker"));
// const TierPicker = React.lazy(() => import("components/AutoComplete/TierPicker"));

const AllLocations = () => {
  const tableRef = useRef(null);

  // const tableRef = createRef();
  const dispatch = useDispatch();

  const [rowDataLoc, setRowDataLoc] = useState({});
  const [open, setOpen] = useState(false);
  const [tier, setTier] = useState({});

  // Add state to track whether or not to show the tier error
  const [showTierError, setShowTierError] = useState(false);

  const locations = useSelector((state) => state.locations.locations);
  const error = useSelector((state) => state.locations.error);
  const user = useSelector((state) => state.users.user);
  const loading = useSelector((state) => state.locations.loading);

  const filteredLocations = useMemo(() => {
    if (user.role !== "partner" || !user.partnerPower) return locations;
    return locations.filter((loc) => loc.display !== "Draft" && loc.display !== "Pending Approval");
  }, [locations, user.role, user.partnerPower]);

  const handlePublishUnPublishLoc = useCallback(
    (val) => {
      if (val.status === "published" || val.status === "draft") {
        dispatch(setLoading());
        dispatch(publishUnpublishLoc(val._id, val.status, tier));
      } else {
        setRowDataLoc(val);
        setOpen(true);
      }
    },
    [dispatch, tier]
  );

  const columns = useMemo(() => {
    const base = [
      columnsMap.partnerName,
      columnsMap.locationType,
      columnsMap.partnerField,
      columnsMap.address,
      columnsMap.city,
      columnsMap.ndOfRooms,
      columnsMap.creationDateNonAsync,
      columnsMap.statusDisplay,
      columnsMap.tier,
    ];
    if (user.role === "partner") {
      // filter out partnerField
      return base.filter((col) => col !== columnsMap.partnerField);
    }
    return base;
  }, [user.role]);
  useEffect(() => {
    dispatch(setLoading());
    if (user.role === "partner") {
      dispatch(getUserPartnerLocations());
    } else if (user.role === "admin") dispatch(getLocations());
  }, [user?.role]);

  useEffect(() => {
    if (error) {
      dispatch(setAlert(error, "error"));
      clearErrors();
    }
  }, [error, setAlert]);

  const handleClose = () => {
    setOpen(false);
    setTier(null);
    setShowTierError(false);
  };

  // When user picks a tier, clear the error
  const handleTierChange = (newValue) => {
    setTier(newValue);
    if (newValue?.value) {
      setShowTierError(false);
    }
  };

  const getCsvFn = useCallback(() => {
    const base = `stats/csv?limit=${locations.length}&page=1&search=&sort=name&dir=asc`;
    const withPartner =
      user.role === "partner" ? `${base}&partner_id=${user.partner._id || user.partner}` : base;
    dispatch(getCsv(`${withPartner}`, "Locations"));
  }, [dispatch, locations.length, user.role, user.partner]);

  const getPdfFn = useCallback(
    (value) => {
      if (!value) return;
      const base = `stats/pdf?limit=${locations.length}&page=1&search=&sort=name&dir=asc`;
      const withPartner =
        user.role === "partner" ? `${base}&partner_id=${user.partner._id || user.partner}` : base;
      dispatch(
        getPdf(
          value,
          `${withPartner}&fields=name,partner,geoLocationData.address,geoLocationData.city,numRooms,createdAt`,
          "Locations"
        )
      );
    },
    [dispatch, locations.length, user.role, user.partner]
  );

  return (
    <div>
      <DashboardLayout>
        <DashboardNavbar />
        <Grid container spacing={3} pt={3}>
          <Grid size={{ xs: 12 }}>
            {(user.role === "partner" || user.role === "admin") && (
              <MaterialTablePicker
                screen="LocationsList"
                title="Locations"
                searchField="locations"
                exportMenuField="locations"
                onRowClickURL={`/${user.role}/locations`}
                currentUrl="locations"
                tableRef={tableRef}
                tableInside
                columns={columns}
                data={
                  user?.role === "partner" && user?.partnerPower ? filteredLocations : locations
                }
                isLoading={loading}
                filteringOptions={false}
                addUrl="locations/new-location"
                actionsName="Location"
                handlePublishUnPublishLoc={handlePublishUnPublishLoc}
                // actionsChild={actionsChildFn}
                editableRole={user.role === "admin" || user.role === "partner"}
                actionsRole={user.role === "admin" || user.role === "partner"}
                getCsv={getCsvFn}
                getPdf={getPdfFn}
              />
            )}
          </Grid>
          <div>
            <Dialog
              open={open}
              onClose={handleClose}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle id="alert-dialog-title">Add Tier: </DialogTitle>

              <DialogContent>
                <Grid size={{ xs: 12 }} mt={3}>
                  <TierPicker
                    tierV={tier || {}}
                    setTier={handleTierChange}
                    setFieldValue={() => {}}
                  />
                  {showTierError && !tier?.value && <Danger validation="Tier is required!" />}
                </Grid>
              </DialogContent>
              <DialogActions>
                <MDButton
                  sx={{
                    border: "2px solid grey",
                    borderRadius: "8px",
                    padding: "8px 16px",
                  }}
                  onClick={() => {
                    if (!tier?.value) {
                      setShowTierError(true);
                    } else {
                      dispatch(publishUnpublishLoc(rowDataLoc?._id, rowDataLoc?.status, tier));
                      handleClose();
                    }
                  }}
                >
                  Publish
                </MDButton>
              </DialogActions>
            </Dialog>
          </div>
        </Grid>
        <Footer />
      </DashboardLayout>
    </div>
  );
};

export default AllLocations;
